import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ACCEPT_XLSX_FILE } from "components/Table/constants";
import CrossIcon from "assets/Icons/replenishment/CrossIcon.svg";
import UploadFileIcon from "assets/Icons/replenishment/UploadFileIcon.svg";
import UploadFileButton from "assets/Icons/replenishment/UploadFileButton.svg";
import { Box, Typography } from "@mui/material";
import FileBox from "./FileBox";
import Button from "components/Button";
import { apiClient } from "api";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import I18nKey from "translations/I18nKey";
import { Tune } from "@mui/icons-material";
import { AppState } from "store/types";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { UseAppDispatch } from "store";
import { changedRangerRefetch } from "store/ranger/ranger.slice";

interface FileUploadProps {
  handleClose: () => void;
  closeUploadModal: (close: boolean) => void;
  widgetName?: string;
}

const FileUpload = ({
  handleClose,
  closeUploadModal,
  widgetName,
}: FileUploadProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const filters = useSelector((state: AppState) => state.table.columnFilters);
  const uploadSellerSelected = useSelector(
    (state: AppState) => state.catalogTracker.uploadSellerSelected
  );
  const [files, setFiles] = useState<File[]>([]);
  const [fileChanged, setFileChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [resp, setResp] = useState<any>({});
  const removeFile = (name: string) => {
    const newFiles = files.filter((item: File) => item.name !== name);
    if (!newFiles.length) {
      //@ts-ignore
      setFiles("");
      setResp({});
    } else setFiles(newFiles);
  };

  const uploadData = async () => {
    setLoading(true);
    let data;
    if (widgetName === "Replenishment")
      data = await apiClient
        .replenishmentUpload(files[0])
        .finally(() => setLoading(false));
    if (widgetName === "PNLEdit")
      data = await apiClient
        .pnlEditUpload(files[0])
        .finally(() => setLoading(false));
    if (widgetName === "Forcasting")
      data = await apiClient
        .forcastinUpload(files[0])
        .catch((err) => {
          console.log(err);
          if (err) {
            const obj = {
              error: "Invalid file upload!",
            };
            data = obj;
            setResp(data);
          }
        })
        .finally(() => setLoading(false));
    if (widgetName === "mismatchSummary")
      data = await apiClient
        .catalogTrackerUpload(files[0], uploadSellerSelected)
        .catch((err) => {
          if (err) {
            const obj = {
              error:
                err?.response &&
                err?.response?.data &&
                err?.response?.data?.errors
                  ? err?.response?.data?.errors
                  : "Invalid file upload!",
            };
            data = obj;
            setResp(data);
          }
        })
        .finally(() => setLoading(false));

    if (widgetName === "Ranger") {
      try {
        data = await apiClient.uploadRangerData(files[0]);
        setResp(data);
      } catch (err: any) {
        console.log(err);

        let errorMessage = "Something went wrong, please try again!";

        if (err.response && err.response.status === 500) {
          errorMessage = "Invalid file upload!";
        } else if (
          err.response &&
          err.response.data &&
          err.response.data.errors
        ) {
          errorMessage = err.response.data.errors;
        }

        data = { error: errorMessage };
        setResp(data);
      } finally {
        setLoading(false);
      }
    }

    if (widgetName === "RangerDimension")
      data = await apiClient
        .dimensionImprovementUpload(files[0])
        .catch((err) => {
          console.log(err);
          if (err) {
            const obj = {
              error: "Invalid file upload!",
            };
            data = obj;
            setResp(data);
          }
        })
        .finally(() => setLoading(false));
    if (widgetName === "RangerFBA")
      data = await apiClient
        .FBAMeasurementUpload(files[0])
        .catch((err) => {
          console.log(err);
          if (err) {
            const obj = {
              error: "Invalid file upload!",
            };
            data = obj;
            setResp(data);
          }
        })
        .finally(() => setLoading(false));
    if (widgetName === "lookup")
      data = await apiClient
        .uploadLookup(files[0])
        .catch((err) => {
          console.log(err);
          if (err) {
            const obj = {
              error: "Invalid file upload!",
            };
            data = obj;
            setResp(data);
          }
        })
        .finally(() => setLoading(false));
    if (data) {
      setResp(data);
    }
    if (widgetName === "AmazonUploadData") {
      data = await apiClient
        .amazonRateTemplateUpload(files[0])
        .then((res) => {
          setResp("File successfully uploaded!");
        })
        .catch((err) => {
          console.log(err);
          if (err) {
            const obj = {
              error: "Invalid file upload!",
            };
            data = obj;
            setResp(data);
          }
        })
        .finally(() => setLoading(false));
    }
    return data;
  };

  useEffect(() => {
    if (files) setFileChanged(true);
  }, [files]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
  });

  return (
    <Box>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            sx={{
              fontFamily: "Lato",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Upload file
          </Typography>
          <Box sx={{ cursor: "pointer" }} onClick={handleClose}>
            <img src={CrossIcon} alt="cross" />
          </Box>
        </Box>

        <Box
          sx={{
            border: "1px dashed #0D47A1",
            height: files.length > 0 ? "auto" : "143px",
            borderRadius: "8px",
            margin: "32px 20px 12px 20px",
            flexDirection: "row",
            display: "flex",
            alignItems: "center",
            justifyContent: files.length ? "end" : "center",
          }}
        >
          {files.length > 0 ? (
            files.map((item: File) => (
              <FileBox
                widgetName={widgetName}
                removeFile={removeFile}
                key={item.name}
                data={item}
                startUpload={loading}
                // stopUpload={isFetched}
                response={resp}
                files={files}
                fileChanged={fileChanged}
              />
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <div
                {...getRootProps()}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <input {...getInputProps()} accept={ACCEPT_XLSX_FILE} />
                <Box>
                  <img src={UploadFileIcon} alt="upload" />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    margin: "16px 0",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Lato",
                      fontSize: "14px",
                      color: "rgba(0, 0, 0, 0.6)",
                      fontWeight: "400",
                    }}
                  >
                    Drop file here
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Lato",
                      fontSize: "14px",
                      fontWeight: "400",
                    }}
                  >
                    or
                  </Typography>
                  <Box>
                    <img src={UploadFileButton} alt="button" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Lato",
                        fontSize: "11px",
                        fontWeight: "400",
                        color: "#BDBDBD",
                        textAlign: "center",
                      }}
                    >
                      File Type: xlsx
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Lato",
                        fontSize: "11px",
                        fontWeight: "400",
                        color: "#BDBDBD",
                      }}
                    >
                      Maximum upload file size: 10 MB
                    </Typography>
                  </Box>
                </Box>
              </div>
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        {files.length > 0 && (
          <Button
            onClick={() => {
              if (_.isEmpty(resp)) uploadData();
              else if (!_.isEmpty(resp) && resp?.error) {
                closeUploadModal(false);
              } else if (
                (!_.isEmpty(resp) && resp?.data?.fba_measurement_details) ||
                (!_.isEmpty(resp) &&
                  resp?.data?.fba_dimension_improvement_details)
              ) {
                closeUploadModal(true);
                //dispatch(changedRangerRefetch(true));
              } else if (!_.isEmpty(resp) && resp.errors) {
                closeUploadModal(false);
              } else closeUploadModal(true);
            }}
            secondary={true}
            primary={false}
            loading={loading}
            text={intl.formatMessage({
              id: _.isEmpty(resp) ? I18nKey.UPLOAD : I18nKey.OK,
            })}
            width="50px"
          />
        )}
      </Box>
    </Box>
  );
};

export default FileUpload;
